import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { kanbanApiService } from '../../services/kanbanApi';
import { KANBAN_LIST_ROLES, LIST_ROLE_CONFIG, type KanbanList, type KanbanListRole } from '../../types/kanban';
import { cn } from '../../utils/cn';

interface EditListModalProps {
  isOpen: boolean;
  onClose: () => void;
  list: KanbanList;
  onUpdated: () => void;
}

export const EditListModal = ({ isOpen, onClose, list, onUpdated }: EditListModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [title, setTitle] = useState('');
  const [wipLimit, setWipLimit] = useState('');
  const [listRole, setListRole] = useState<KanbanListRole>('none');
  const [policies, setPolicies] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setIsSubmitting(false);
    setTitle(list.title);
    setWipLimit(list.wip_limit === null || list.wip_limit === undefined ? '' : String(list.wip_limit));
    setListRole(list.list_role);
    setPolicies(list.policies ?? '');
  }, [isOpen, list]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      setError('Title is required.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const wipParsed = wipLimit.trim() === '' ? null : Number(wipLimit);
    const wip =
      wipParsed === null || Number.isNaN(wipParsed) ? null : Math.max(0, Math.floor(wipParsed));

    try {
      await kanbanApiService.updateList(list.id, {
        title: trimmed,
        wip_limit: wip,
        list_role: listRole,
        policies: policies.trim() || null,
      });
      onUpdated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update list.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          type="button"
          onClick={onClose}
          className="modal-close z-10"
          aria-label="Close"
          disabled={isSubmitting}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="modal-header">
          <h2 className="modal-title">Edit list</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="edit-list-title" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Title
            </label>
            <input
              id="edit-list-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full"
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label htmlFor="edit-list-wip" className="text-xs font-medium text-glass-400 mb-1.5 block">
              WIP limit
            </label>
            <input
              id="edit-list-wip"
              type="number"
              min={0}
              value={wipLimit}
              onChange={(e) => setWipLimit(e.target.value)}
              className="glass-input w-full"
              placeholder="Unlimited"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="edit-list-role" className="text-xs font-medium text-glass-400 mb-1.5 block">
              List role
            </label>
            <select
              id="edit-list-role"
              value={listRole}
              onChange={(e) => setListRole(e.target.value as KanbanListRole)}
              className="glass-input w-full"
              disabled={isSubmitting}
            >
              {KANBAN_LIST_ROLES.map((r) => (
                <option key={r} value={r}>
                  {LIST_ROLE_CONFIG[r].label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="edit-list-policies" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Policies
            </label>
            <textarea
              id="edit-list-policies"
              value={policies}
              onChange={(e) => setPolicies(e.target.value)}
              className="glass-input w-full min-h-[80px] resize-y"
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="glass-button-secondary" disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="submit"
              className={cn('btn-primary', isSubmitting && 'opacity-70')}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
